import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { BrowserProvider, formatEther, type Eip1193Provider } from 'ethers'
import { getProviderById, type ProviderId } from '@/lib/wallet/providers'
import { walletService } from '@/lib/wallet/walletService'
import type { WalletState, WalletType, ChainId } from '@/types'

// walletService imports useWalletStore (`.getState()` access inside methods)
// — and we call walletService.setProvider / tryRebind here. Both modules only
// touch each other inside callbacks, so the cycle is safe at init. The static
// import is intentional: walletService is also statically imported by 7 other
// modules (ChainPicker, Navbar, useBalance, useWallet, siwe, etc.), so the
// dynamic import that used to live here couldn't move it into its own chunk
// and only added an INEFFECTIVE_DYNAMIC_IMPORT build warning.

// ─── Provider ID → WalletType mapping ────────────────────────────────────────

const PROVIDER_WALLET_TYPE: Record<ProviderId, WalletType> = {
  metamask: 'metamask',
  coinbase: 'coinbase',
  rabby: 'rabby',
  brave: 'brave',
  'generic-evm': 'injected',
  freighter: 'freighter',
  lobstr: 'lobstr',
}

// ─── Store interface ──────────────────────────────────────────────────────────

interface WalletStore extends WalletState {
  setConnecting: (isConnecting: boolean) => void
  setConnected: (
    address: string,
    chainId: ChainId,
    walletType: WalletType,
    providerName?: string
  ) => void
  setChain: (chainId: ChainId) => void
  setBalance: (balance: string) => void
  disconnect: () => void
  /** Connect via a detected provider. Updates walletService for sign/switch ops. */
  connect: (providerId: ProviderId) => Promise<void>
  /** Fetch and store the native token balance for the connected address. */
  getBalance: () => Promise<void>
  /** Mark rehydration as complete after tryRebind confirms provider is available. */
  setRehydrationComplete: () => void
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: WalletState = {
  address: null,
  chainId: null,
  walletType: null,
  providerName: null,
  isConnected: false,
  isConnecting: false,
  isRebinding: false,
  balance: null,
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useWalletStore = create<WalletStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setConnecting: (isConnecting) => set({ isConnecting }),

      setConnected: (address, chainId, walletType, providerName) =>
        set({
          address,
          chainId,
          walletType,
          providerName: providerName ?? null,
          isConnected: true,
          isConnecting: false,
        }),

      setChain: (chainId) => set({ chainId }),

      setBalance: (balance) => set({ balance }),

      disconnect: () => set({ ...initialState }),

      setRehydrationComplete: () => set({ isRebinding: false }),

      connect: async (providerId: ProviderId) => {
        set({ isConnecting: true })
        try {
          const provider = getProviderById(providerId)
          if (!provider) throw new Error(`Provider "${providerId}" not found`)
          if (!provider.isDetected) throw new Error(`${provider.name} is not installed`)

          // Trigger wallet popup → returns checksummed address or Stellar pubkey
          const address = await provider.connect()

          let chainId: ChainId = 1
          const rawProvider = provider.getEIP1193Provider()

          if (rawProvider) {
            // EVM: read current chain from the wallet
            const hex = (await rawProvider.request({ method: 'eth_chainId' })) as string
            chainId = parseInt(hex, 16) as ChainId

            walletService.setProvider(rawProvider)
          } else {
            // Stellar wallets
            chainId = 'stellar-mainnet'
          }

          set({
            address,
            chainId,
            walletType: PROVIDER_WALLET_TYPE[providerId],
            providerName: provider.name,
            isConnected: true,
            isConnecting: false,
          })

          // Kick off balance fetch asynchronously (don't block connection)
          get().getBalance()
        } catch (err) {
          set({ isConnecting: false })
          throw err
        }
      },

      getBalance: async () => {
        const { address, chainId, isConnected } = get()
        if (!isConnected || !address) return
        try {
          if (typeof chainId === 'string') {
            // Stellar — balance handled by stellarService, skip here
            return
          }
          const ep = walletService.ethersProvider
          if (!ep) {
            // Fallback: use window.ethereum directly
            if (!window.ethereum) return
            const fallback = new BrowserProvider(window.ethereum as Eip1193Provider)
            const bal = await fallback.getBalance(address)
            set({ balance: formatEther(bal) })
            return
          }
          const bal = await ep.getBalance(address)
          set({ balance: formatEther(bal) })
        } catch (err) {
          // Balance is non-critical but log for debugging
          console.warn('[walletStore] Balance fetch failed:', err)
        }
      },
    }),
    {
      name: 'saasfarm-wallet',
      partialize: (state) => ({
        address: state.address,
        chainId: state.chainId,
        walletType: state.walletType,
        providerName: state.providerName,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.address) {
          // Mark as rebinding so the UI knows the wallet is in a transitional state
          state.isConnected = true
          state.isRebinding = true

          if (typeof window !== 'undefined') {
            setTimeout(() => {
              const success = walletService.tryRebind(state.walletType ?? undefined)
              if (success) {
                state.isRebinding = false
              } else {
                // Provider not available — clear persisted state
                console.warn('[walletStore] tryRebind failed — clearing persisted wallet state')
                state.disconnect()
              }
            }, 0)
          }
        }
      },
    }
  )
)
